import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Target,
  Users,
  MessageSquare,
  Brain,
  Clock,
  Award,
  TrendingUp,
  BookOpen,
  CheckCircle2,
  ChevronRight,
  Briefcase,
  GraduationCap,
  Rocket,
  Building2,
  Lightbulb,
  Shield,
  BarChart3,
  Heart,
  Zap
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

export const LeadershipManagementTraining = () => {
  const navigate = useNavigate();

  const pageData = {
    h1: 'Leadership and Management Training',
    metaTitle: 'Leadership and Management Training | Develop Effective Business Leaders',
    metaDescription: 'Improve communication, strategic thinking, and people management with leadership and management training.',
    metaKeywords: 'leadership training, management training, leadership development, people management, strategic thinking, team building, executive training',
    canonicalUrl: 'https://isde.academy/training/leadership-and-management-training'
  };

  const coreComponents = [
    {
      id: 'leadership-skills',
      title: 'Leadership Skills Development',
      icon: Target,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      gradient: 'from-blue-500 to-cyan-600',
      description: 'Leadership training focuses on inspiring and influencing people to achieve organizational goals.',
      topics: [
        'Vision and goal setting',
        'Leadership styles and adaptability',
        'Emotional intelligence and self-awareness',
        'Ethical leadership and integrity'
      ]
    },
    {
      id: 'people-management',
      title: 'People Management Skills',
      icon: Users,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      gradient: 'from-purple-500 to-pink-600',
      description: 'Effective managers understand how to manage individuals and teams to drive performance.',
      topics: [
        'Team building and motivation',
        'Performance management',
        'Conflict management and negotiation',
        'Coaching and mentoring'
      ]
    },
    {
      id: 'communication',
      title: 'Communication and Interpersonal Skills',
      icon: MessageSquare,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      gradient: 'from-emerald-500 to-teal-600',
      description: 'Strong communication is the foundation of leadership success in any organization.',
      topics: [
        'Effective verbal and non-verbal communication',
        'Active listening',
        'Presentation and public speaking skills',
        'Giving and receiving feedback'
      ]
    },
    {
      id: 'strategic-thinking',
      title: 'Strategic Thinking and Decision Making',
      icon: Brain,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      gradient: 'from-amber-500 to-orange-600',
      description: 'Leadership and management training enhances analytical and strategic decision-making abilities.',
      topics: [
        'Analytical thinking',
        'Risk assessment and problem solving',
        'Decision-making under pressure',
        'Business and organizational strategy'
      ]
    },
    {
      id: 'change-management',
      title: 'Change and Time Management',
      icon: Clock,
      color: 'text-rose-600',
      bgColor: 'bg-rose-50',
      gradient: 'from-rose-500 to-red-600',
      description: 'Managers must adapt quickly to change and manage their time effectively.',
      topics: [
        'Change management principles',
        'Time management and prioritization',
        'Stress management and resilience',
        'Leading teams through uncertainty'
      ]
    }
  ];

  const benefits = [
    {
      title: 'Improved Team Performance',
      description: 'Trained leaders motivate employees, resolve conflicts effectively, and drive high levels of engagement and productivity.',
      icon: TrendingUp,
      color: 'blue'
    },
    {
      title: 'Enhanced Decision-Making Ability',
      description: 'Leadership and management training develops analytical thinking and problem-solving skills, enabling better decisions at all levels.',
      icon: Brain,
      color: 'purple'
    },
    {
      title: 'Strong Organizational Culture',
      description: 'Leaders shape workplace culture. Training promotes ethical behavior, accountability, and positive work environments.',
      icon: Building2,
      color: 'emerald'
    },
    {
      title: 'Increased Employee Retention',
      description: 'Employees are more likely to stay when they feel supported by capable managers and inspiring leaders.',
      icon: Users,
      color: 'amber'
    },
    {
      title: 'Career Growth and Confidence',
      description: 'For individuals, leadership training boosts confidence, communication skills, and career advancement opportunities.',
      icon: Award,
      color: 'rose'
    }
  ];

  const whoShouldAttend = [
    {
      title: 'Senior & Middle-Level Managers',
      description: 'Enhance strategic leadership and organizational management capabilities',
      icon: Building2,
      color: 'blue'
    },
    {
      title: 'Team Leaders & Supervisors',
      description: 'Develop people management and team motivation skills',
      icon: Users,
      color: 'purple'
    },
    {
      title: 'High-Potential Employees',
      description: 'Prepare for future leadership roles and responsibilities',
      icon: TrendingUp,
      color: 'emerald'
    },
    {
      title: 'Entrepreneurs & Business Owners',
      description: 'Lead business growth and build high-performing teams',
      icon: Rocket,
      color: 'amber'
    },
    {
      title: 'Project Managers',
      description: 'Manage projects, resources, and stakeholders effectively',
      icon: Target,
      color: 'rose'
    },
    {
      title: 'Aspiring Leaders',
      description: 'Build foundational leadership and management competencies',
      icon: GraduationCap,
      color: 'indigo'
    }
  ];

  const leadershipVsManagement = [
    {
      aspect: 'Focus',
      leadership: 'Inspires and motivates',
      management: 'Plans and organizes'
    },
    {
      aspect: 'Approach',
      leadership: 'Focuses on vision',
      management: 'Focuses on execution'
    },
    {
      aspect: 'Style',
      leadership: 'Influences people',
      management: 'Manages processes'
    },
    {
      aspect: 'Impact',
      leadership: 'Drives change',
      management: 'Ensures stability'
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "Leadership and Management Training",
    "description": "Professional development program designed to enhance leadership abilities, people management, strategic thinking, and organizational effectiveness.",
    "provider": {
      "@type": "EducationalOrganization",
      "name": "ISDE Academy",
      "url": "https://isde.academy"
    },
    "courseCode": "LEAD-MGMT",
    "educationalLevel": "Professional Development",
    "teaches": [
      "Leadership Skills Development",
      "People Management",
      "Communication and Interpersonal Skills",
      "Strategic Thinking and Decision Making",
      "Change and Time Management"
    ],
    "availableLanguage": "English"
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is leadership and management training?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Leadership and management training is a professional development program designed to enhance an individual's ability to lead teams, manage operations, and achieve organizational goals. While leadership focuses on vision, influence, and motivation, management emphasizes planning, organizing, and controlling resources."
        }
      },
      {
        "@type": "Question",
        "name": "Who should attend leadership and management training?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Leadership and management training is ideal for senior and middle-level managers, team leaders and supervisors, high-potential employees, entrepreneurs and business owners, project managers, and aspiring leaders. The training can be customized based on experience level, industry, and organizational needs."
        }
      },
      {
        "@type": "Question",
        "name": "What are the benefits of leadership and management training?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Key benefits include improved team performance, enhanced decision-making ability, strong organizational culture, increased employee retention, and career growth and confidence for individuals."
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>{pageData.metaTitle}</title>
        <meta name="description" content={pageData.metaDescription} />
        <meta name="keywords" content={pageData.metaKeywords} />
        <link rel="canonical" href={pageData.canonicalUrl} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageData.metaTitle} />
        <meta property="og:description" content={pageData.metaDescription} />
        <meta property="og:url" content={pageData.canonicalUrl} />
        <meta property="og:site_name" content="ISDE Academy" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageData.metaTitle} />
        <meta name="twitter:description" content={pageData.metaDescription} />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 text-white py-24 mt-20 overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 -left-4 w-72 h-72 bg-white rounded-full mix-blend-overlay filter blur-xl animate-blob"></div>
            <div className="absolute top-0 -right-4 w-72 h-72 bg-white rounded-full mix-blend-overlay filter blur-xl animate-blob animation-delay-2000"></div>
            <div className="absolute -bottom-8 left-20 w-72 h-72 bg-white rounded-full mix-blend-overlay filter blur-xl animate-blob animation-delay-4000"></div>
          </div>

          <div className="absolute top-10 right-10 opacity-20">
            <Target className="h-32 w-32" />
          </div>
          <div className="absolute bottom-10 left-10 opacity-20">
            <Users className="h-24 w-24" />
          </div>

          <div className="max-w-7xl mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center max-w-4xl mx-auto"
            >
              <motion.div
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <Award className="h-5 w-5 text-yellow-200" />
                <span className="text-sm font-semibold">Develop Effective Business Leaders</span>
              </motion.div>

              <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                {pageData.h1}
              </h1>

              <p className="text-xl text-blue-100 mb-8 leading-relaxed max-w-3xl mx-auto">
                In today's fast-changing business environment, organizations need leaders who can inspire, manage change, and deliver results. Leadership and management training plays a critical role in building confident leaders, effective managers, and high-performing teams.
              </p>

              <div className="flex flex-wrap gap-4 justify-center">
                <Button
                  onClick={() => navigate('/contact')}
                  className="bg-white text-blue-600 hover:bg-blue-50 font-semibold px-8 py-6 text-lg shadow-xl hover:shadow-2xl transition-all"
                >
                  Enroll Now
                </Button>
                <Button
                  onClick={() => navigate('/training-calendar')}
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-blue-600 font-semibold px-8 py-6 text-lg"
                >
                  View Schedule
                </Button>
              </div>
            </motion.div>
          </div>

          <div className="absolute bottom-0 left-0 right-0">
            <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#F9FAFB"/>
            </svg>
          </div>
        </section>

        {/* What Is Section */}
        <section className="max-w-7xl mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">What Is Leadership and Management Training?</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Leadership and management training is a professional development program designed to enhance an individual's ability to lead teams, manage operations, and achieve organizational goals. While leadership focuses on vision, influence, and motivation, management emphasizes planning, organizing, and controlling resources. Effective training integrates both aspects to create well-rounded professionals.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              This training is essential for organizations seeking sustainable growth, improved productivity, and a strong workplace culture.
            </p>
          </div>
        </section>

        {/* Why Essential Section */}
        <section className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">Essence of Leadership and Management Training Is Important</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-8 max-w-4xl">
              Modern workplaces are complex, diverse, and dynamic. Leaders are expected to handle change, manage people effectively, and align teams with organizational objectives. Leadership and management training helps professionals:
            </p>

            <div className="grid md:grid-cols-2 gap-4 mb-8 max-w-4xl">
              <div className="flex items-start gap-2">
                <ChevronRight className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Improve people management and team performance</span>
              </div>
              <div className="flex items-start gap-2">
                <ChevronRight className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Enhance communication and conflict resolution skills</span>
              </div>
              <div className="flex items-start gap-2">
                <ChevronRight className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Build emotional intelligence and resilience</span>
              </div>
              <div className="flex items-start gap-2">
                <ChevronRight className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Develop strategic thinking and problem-solving abilities</span>
              </div>
              <div className="flex items-start gap-2">
                <ChevronRight className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Adapt to change and lead transformation initiatives</span>
              </div>
            </div>

            <p className="text-gray-700 italic max-w-4xl">
              Organizations that invest in leadership development consistently outperform competitors in employee engagement, innovation, and profitability.
            </p>
          </div>
        </section>

        {/* Core Components */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h3 className="text-3xl md:text-4xl font-bold mb-12 text-center text-gray-900">Core Components of Leadership and Management Training</h3>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {coreComponents.map((component, index) => {
                const IconComponent = component.icon;
                return (
                  <motion.article
                    key={component.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="h-full overflow-hidden hover:shadow-xl transition-all hover:-translate-y-2 group">
                      <div className={`bg-gradient-to-br ${component.gradient} p-6 text-white`}>
                        <div className={`${component.bgColor} w-16 h-16 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                          <IconComponent className={`h-8 w-8 ${component.color}`} />
                        </div>
                        <h4 className="text-2xl font-bold mb-2">
                          {index + 1}. {component.title}
                        </h4>
                        <p className="text-white/90 text-sm leading-relaxed mb-4">
                          {component.description}
                        </p>
                      </div>
                      <CardContent className="p-6 bg-white">
                        <h5 className="font-bold text-sm text-gray-900 mb-3 uppercase tracking-wide">Key Areas:</h5>
                        <ul className="space-y-2">
                          {component.topics.map((topic, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <CheckCircle2 className={`h-4 w-4 ${component.color} flex-shrink-0 mt-0.5`} />
                              <span className="text-gray-600 text-sm">{topic}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  </motion.article>
                );
              })}
            </div>
          </div>
        </section>

        {/* Who Should Attend */}
        <section className="bg-gradient-to-br from-blue-50 to-indigo-50 py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h3 className="text-3xl md:text-4xl font-bold mb-12 text-center text-gray-900">Who Should Attend Leadership and Management Training?</h3>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {whoShouldAttend.map((audience, index) => {
                const IconComponent = audience.icon;
                const colors = {
                  blue: { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-200' },
                  purple: { bg: 'bg-purple-100', text: 'text-purple-600', border: 'border-purple-200' },
                  emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600', border: 'border-emerald-200' },
                  amber: { bg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-200' },
                  rose: { bg: 'bg-rose-100', text: 'text-rose-600', border: 'border-rose-200' },
                  indigo: { bg: 'bg-indigo-100', text: 'text-indigo-600', border: 'border-indigo-200' }
                };
                const color = colors[audience.color];

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className={`h-full bg-white border-2 ${color.border} hover:shadow-xl transition-all hover:-translate-y-2`}>
                      <CardContent className="p-6 text-center">
                        <div className={`${color.bg} w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4`}>
                          <IconComponent className={`h-8 w-8 ${color.text}`} />
                        </div>
                        <h5 className="text-xl font-bold mb-3 text-gray-900">{audience.title}</h5>
                        <p className="text-gray-600 text-sm">{audience.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>

            <p className="text-center text-gray-700 max-w-3xl mx-auto">
              The training can be customized based on experience level, industry, and organizational needs.
            </p>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h3 className="text-3xl md:text-4xl font-bold mb-12 text-center text-gray-900">Pros of Leadership and Management Training</h3>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((benefit, index) => {
                const IconComponent = benefit.icon;
                const colors = {
                  blue: { bg: 'bg-blue-50', text: 'text-blue-600' },
                  purple: { bg: 'bg-purple-50', text: 'text-purple-600' },
                  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600' },
                  amber: { bg: 'bg-amber-50', text: 'text-amber-600' },
                  rose: { bg: 'bg-rose-50', text: 'text-rose-600' }
                };
                const color = colors[benefit.color];

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="h-full hover:shadow-lg transition-all hover:-translate-y-1">
                      <CardContent className="p-6">
                        <div className={`${color.bg} w-12 h-12 rounded-lg flex items-center justify-center mb-4`}>
                          <IconComponent className={`h-6 w-6 ${color.text}`} />
                        </div>
                        <h5 className="text-xl font-bold mb-2 text-gray-900">{index + 1}. {benefit.title}</h5>
                        <p className="text-gray-600">{benefit.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Leadership vs Management */}
        <section className="py-16">
          <div className="max-w-5xl mx-auto px-4">
            <h4 className="text-3xl md:text-4xl font-bold mb-6 text-center text-gray-900">Leadership vs Management: Understanding the Difference</h4>
            <p className="text-lg text-gray-700 mb-8 text-center max-w-3xl mx-auto">
              While often used together, leadership and management are distinct.
            </p>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse bg-white shadow-lg rounded-lg overflow-hidden">
                <thead>
                  <tr className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
                    <th className="p-4 text-left font-bold">Aspect</th>
                    <th className="p-4 text-left font-bold">Leadership</th>
                    <th className="p-4 text-left font-bold">Management</th>
                  </tr>
                </thead>
                <tbody>
                  {leadershipVsManagement.map((row, index) => (
                    <tr key={index} className={`border-b ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}>
                      <td className="p-4 font-semibold text-gray-900">{row.aspect}</td>
                      <td className="p-4 text-gray-700">{row.leadership}</td>
                      <td className="p-4 text-gray-700">{row.management}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-center text-gray-700 mt-6 italic">
              Leadership and management training integrates both to create balanced professionals who can inspire people and deliver results.
            </p>
          </div>
        </section>

        {/* Measuring Impact */}
        <section className="bg-gradient-to-br from-gray-50 to-blue-50 py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h4 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">Measuring the Impact of Leadership and Management Training</h4>
            <p className="text-lg text-gray-700 mb-8 max-w-3xl">
              Training effectiveness can be measured through:
            </p>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: TrendingUp, title: 'Employee performance improvements', color: 'blue' },
                { icon: Users, title: 'Feedback and engagement scores', color: 'purple' },
                { icon: BarChart3, title: 'Team productivity metrics', color: 'emerald' },
                { icon: Award, title: 'Leadership competency assessments', color: 'amber' }
              ].map((metric, index) => {
                const IconComponent = metric.icon;
                const colors = {
                  blue: { bg: 'bg-blue-100', text: 'text-blue-600' },
                  purple: { bg: 'bg-purple-100', text: 'text-purple-600' },
                  emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600' },
                  amber: { bg: 'bg-amber-100', text: 'text-amber-600' }
                };
                const color = colors[metric.color];

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="bg-white hover:shadow-lg transition-shadow">
                      <CardContent className="p-6 text-center">
                        <div className={`${color.bg} w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4`}>
                          <IconComponent className={`h-8 w-8 ${color.text}`} />
                        </div>
                        <p className="font-semibold text-gray-900">{metric.title}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>

            <p className="text-gray-700 mt-8 text-center max-w-3xl mx-auto">
              Organizations that track results gain better ROI from training investments.
            </p>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h4 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">Why Choose our Leadership and Management Training programme?</h4>
            <p className="text-lg text-gray-700 mb-8 max-w-3xl">
              Professional training providers offer:
            </p>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: Award, title: 'Experienced leadership trainers', color: 'blue' },
                { icon: Lightbulb, title: 'Proven training frameworks', color: 'emerald' },
                { icon: BookOpen, title: 'Practical, real-world case studies', color: 'purple' },
                { icon: Target, title: 'Measurable outcomes', color: 'amber' }
              ].map((item, index) => {
                const IconComponent = item.icon;
                const colors = {
                  blue: { bg: 'bg-blue-50', text: 'text-blue-600' },
                  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600' },
                  purple: { bg: 'bg-purple-50', text: 'text-purple-600' },
                  amber: { bg: 'bg-amber-50', text: 'text-amber-600' }
                };
                const color = colors[item.color];

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="hover:shadow-lg transition-shadow">
                      <CardContent className="p-6 flex items-center gap-4">
                        <div className={`${color.bg} p-3 rounded-lg flex-shrink-0`}>
                          <IconComponent className={`h-6 w-6 ${color.text}`} />
                        </div>
                        <p className="font-semibold text-gray-900">{item.title}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>

            <p className="text-gray-700 mt-8 text-lg italic max-w-3xl">
              Choosing expert trainers ensures lasting impact and skill development.
            </p>
          </div>
        </section>

        {/* Future Scope */}
        <section className="bg-gradient-to-br from-blue-50 to-purple-50 py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h4 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">Future Scope of Leadership and Management Training</h4>
            <p className="text-lg text-gray-700 mb-8 max-w-3xl">
              As workplaces evolve, leadership training will focus more on:
            </p>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { icon: Lightbulb, text: 'Digital leadership' },
                { icon: Heart, text: 'Emotional intelligence' },
                { icon: Users, text: 'Diversity and inclusion' },
                { icon: Zap, text: 'Agile and adaptive leadership' }
              ].map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="bg-white border-2 border-blue-200 hover:shadow-lg transition-all hover:-translate-y-1">
                      <CardContent className="p-6 flex items-center gap-3">
                        <IconComponent className="h-8 w-8 text-blue-600 flex-shrink-0" />
                        <p className="font-semibold text-gray-900">{item.text}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>

            <p className="text-gray-700 mt-8 text-center max-w-3xl mx-auto italic">
              Continuous learning is essential for leaders to remain effective in a competitive business landscape.
            </p>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white py-20">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-5xl font-bold mb-6">
                Ready to Develop Your Leadership Skills?
              </h2>
              <p className="text-xl text-blue-100 mb-8">
                Join our comprehensive leadership and management training program and transform into an effective business leader
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => navigate('/contact')}
                  className="bg-white text-blue-600 hover:bg-blue-50 font-semibold px-8 py-6 text-lg shadow-xl hover:shadow-2xl transition-all"
                >
                  Enroll Now
                </Button>
                <Button
                  onClick={() => navigate('/training-calendar')}
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-blue-600 font-semibold px-8 py-6 text-lg"
                >
                  View Training Calendar
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default LeadershipManagementTraining;
